import confetti from "canvas-confetti"

/**
 * OKS-543 — Rank-up confetti seam.
 *
 * A single wrapper around `canvas-confetti` so the celebration modal
 * never imports the library directly. This gives us:
 *
 *   1. **One mock target.** Component + system specs `vi.mock("@/lib/confetti")`
 *      and spy on `fireRankUpConfetti` — the library itself (which needs a
 *      real `<canvas>` unavailable in jsdom) is never invoked in tests.
 *   2. **SSR / environment safety.** The library touches `document`/`window`
 *      at call time; we guard for a browser so an accidental server-side or
 *      pre-hydration call is a no-op instead of a crash. The import is safe
 *      (canvas-confetti defers all DOM work to invocation).
 *   3. **A single source of celebration params** (UX Spec §4).
 *
 * **Reduced motion is NOT gated here.** The component owns that decision
 * (`usePrefersReducedMotion()` → simply does not call this) so there is
 * exactly one observable gate for the spec spy — no double-gating. This
 * wrapper only decides "can we render confetti at all in this environment".
 */

/**
 * UX Spec §4 — one burst, ~90–120 particles, spread ~70°, moderate start
 * velocity, origin just above the crest (`y: 0.35`), decaying within ~1.2s
 * (`ticks`). Palette = brand primary (resolved at call time, best-effort) +
 * amber / sky / emerald accents, matching the shipped particle palette.
 */
const BASE_OPTIONS: confetti.Options = {
  particleCount: 110,
  spread: 70,
  startVelocity: 42,
  ticks: 130,
  gravity: 1,
  scalar: 0.9,
  origin: { y: 0.35 },
}

// Tailwind amber-400 / sky-400 / emerald-400 — the shipped CSS-particle
// accent palette. Brand primary is prepended at call time when it resolves.
const ACCENT_COLORS = ["#fbbf24", "#38bdf8", "#34d399"] as const
const FALLBACK_PRIMARY = "#f97316" // OKS orange — used if `--primary` can't resolve to hex.

/**
 * Resolve the brand's `--primary` design token to a `#rrggbb` hex that
 * canvas-confetti accepts (the library only parses hex). We can't read the
 * raw custom property (it may be authored as `oklch(...)`), so we let the
 * browser compute it: paint `color: var(--primary)` on a detached probe,
 * read back the resolved `rgb(...)`, and convert. Returns `null` when there
 * is no DOM (SSR) or the value can't be resolved — the caller then falls
 * back to {@link FALLBACK_PRIMARY}.
 */
function resolveBrandPrimaryHex(): string | null {
  if (typeof document === "undefined") return null
  const probe = document.createElement("span")
  probe.style.color = "var(--primary)"
  probe.style.display = "none"
  document.body.appendChild(probe)
  const computed = getComputedStyle(probe).color
  probe.remove()

  const match = /^rgba?\(([^)]+)\)$/.exec(computed)
  if (!match) return null
  const [r, g, b] = match[1]
    .split(",")
    .slice(0, 3)
    .map((part) => Number.parseInt(part.trim(), 10))
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return null

  const toHex = (channel: number) => channel.toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Fire the single rank-up confetti burst. No-op outside a browser. Callers
 * gate on `prefers-reduced-motion` before invoking (see module doc).
 */
export function fireRankUpConfetti(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return

  const primary = resolveBrandPrimaryHex() ?? FALLBACK_PRIMARY
  void confetti({
    ...BASE_OPTIONS,
    colors: [primary, ...ACCENT_COLORS],
  })
}
